import { jsPDF } from "jspdf";
import { ATS_CV_PAGE_MIN_HEIGHT, ATS_CV_PAGE_WIDTH } from "./atsCvLayout.js";

/**
 * The preview, redrawn into a PDF as text.
 *
 * The export used to be html2canvas: a picture of the preview, pasted onto the
 * page. It never quite matched — html2canvas places a glyph by its own idea of
 * where a line box puts the baseline, which drifted about 14px down the page by
 * the first section rule — and, worse, a picture of a CV has no words in it, so
 * the one thing this tool promises (a CV an applicant tracking system can read)
 * was exactly what the file could not do.
 *
 * So the browser keeps doing the layout, and this reads the answer back: every
 * line of text is drawn at the rectangle the browser laid it out in, with the
 * font, size and colour it computed. The PDF comes out on top of the preview to
 * within a pixel, and every word in it is selectable, searchable and parseable.
 *
 * Two things cannot be set with the PDF core fonts, and both are drawn as a
 * picture of just that fragment: the contact icons (inline SVG) and any line of
 * Bangla or other non-Latin text.
 */

/** 1 CSS px of the preview is 1 pt of the page: both are A4 at 72dpi. */
const PAGE_W = ATS_CV_PAGE_WIDTH;
const PAGE_H = ATS_CV_PAGE_MIN_HEIGHT;

/** What the PDF core fonts (Times, Helvetica, Courier) can actually set. */
const CORE_FONT_RANGE = /^[\u0000-ɏ -⁯₠-₿℀-⅏]*$/;
/** How finely the fragments that have to be pictures are drawn. */
const RASTER_SCALE = 4;
/**
 * How far a line may be stretched between letters to make up the width Times
 * does not have. Past this the gaps read as spaces to whatever parses the file
 * — an applicant tracking system would see "S U M M A R Y" instead of a section
 * heading — so a wider line is set at its natural width and anchored by its own
 * alignment instead.
 */
const MAX_CHAR_SPACE = 0.45;

const measureCanvas = typeof document === "undefined" ? null : document.createElement("canvas");
const measureCtx = measureCanvas?.getContext("2d") || null;
const metricsCache = new Map();

const fontShorthand = (cs) => `${cs.fontStyle} ${cs.fontWeight} ${parseFloat(cs.fontSize)}px ${cs.fontFamily}`;

/**
 * Ascent and descent of a font, which is what turns a line box into a baseline:
 * jsPDF draws from the baseline, the browser hands out boxes.
 */
function fontMetrics(shorthand) {
    if (metricsCache.has(shorthand)) return metricsCache.get(shorthand);
    let out = { asc: 0, desc: 0 };
    if (measureCtx) {
        measureCtx.font = shorthand;
        const m = measureCtx.measureText("Hxjgy");
        out = { asc: m.fontBoundingBoxAscent || 0, desc: m.fontBoundingBoxDescent || 0 };
    }
    metricsCache.set(shorthand, out);
    return out;
}

/** The nearest core font to what the preview is using. */
function pdfFontFor(cs) {
    const family = cs.fontFamily.toLowerCase();
    const first = family.split(",")[0];
    const base = /mono|courier/.test(family)
        ? "courier"
        : (/georgia|times|garamond|serif/.test(family) && !/sans-serif/.test(first))
            ? "times"
            : "helvetica";
    const bold = cs.fontWeight === "bold" || Number(cs.fontWeight) >= 600;
    const italic = cs.fontStyle === "italic" || cs.fontStyle === "oblique";
    const style = bold && italic ? "bolditalic" : bold ? "bold" : italic ? "italic" : "normal";
    return [base, style];
}

function rgba(value) {
    const parts = String(value).match(/[\d.]+/g) || ["0", "0", "0"];
    return [
        Number(parts[0]) || 0,
        Number(parts[1]) || 0,
        Number(parts[2]) || 0,
        parts[3] === undefined ? 1 : Number(parts[3]),
    ];
}

/**
 * Turn `::before` / `::after` text into real spans.
 *
 * The bullet on every list item is a pseudo-element, and a pseudo-element has
 * no text node — walking the tree would skip every bullet on the page. Copying
 * the pseudo's own position and font keeps it exactly where it was drawn.
 */
function materialisePseudoText(root) {
    for (const el of [root, ...root.querySelectorAll("*")]) {
        for (const which of ["::before", "::after"]) {
            const cs = window.getComputedStyle(el, which);
            const raw = cs.content;
            if (!raw || raw === "none" || raw === "normal") continue;
            const text = raw.replace(/^["'](.*)["']$/s, "$1");
            if (!text.trim()) continue;
            const span = document.createElement("span");
            span.textContent = text;
            span.style.cssText = [
                `position:${cs.position}`,
                `left:${cs.left}`,
                `right:${cs.right}`,
                `top:${cs.top}`,
                `bottom:${cs.bottom}`,
                `font:${cs.font || fontShorthand(cs)}`,
                `color:${cs.color}`,
                `line-height:${cs.lineHeight}`,
                `letter-spacing:${cs.letterSpacing}`,
            ].join(";");
            if (which === "::before") el.insertBefore(span, el.firstChild);
            else el.appendChild(span);
        }
    }
}

/** Draw an inline SVG onto a canvas and hand back a PNG of it. */
async function svgToPng(svg, width, height) {
    const node = svg.cloneNode(true);
    node.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    if (!node.getAttribute("width")) node.setAttribute("width", String(width));
    if (!node.getAttribute("height")) node.setAttribute("height", String(height));
    // `currentColor` means nothing once the node is out of the document, so the
    // colour it was inheriting is written in. Anything the icon left unset is
    // left unset: filling it would turn an outline icon into a black blob.
    const colour = window.getComputedStyle(svg).color;
    // The icon's own element carries `currentColor` too (Tabler sets stroke on the <svg>),
    // so it is resolved along with its children — otherwise a light icon exports as black.
    [node, ...node.querySelectorAll("*")].forEach((child) => {
        if (child.getAttribute("fill") === "currentColor") child.setAttribute("fill", colour);
        if (child.getAttribute("stroke") === "currentColor") child.setAttribute("stroke", colour);
    });

    const xml = new XMLSerializer().serializeToString(node);
    const url = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(xml)))}`;
    const img = new Image();
    await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
    });

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * RASTER_SCALE));
    canvas.height = Math.max(1, Math.round(height * RASTER_SCALE));
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
}

/** A picture of one line of text, for scripts the core fonts have no glyphs for. */
function lineToPng(text, cs, width, height) {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * RASTER_SCALE));
    canvas.height = Math.max(1, Math.round(height * RASTER_SCALE));
    const ctx = canvas.getContext("2d");
    ctx.scale(RASTER_SCALE, RASTER_SCALE);
    ctx.font = fontShorthand(cs);
    ctx.fillStyle = cs.color;
    ctx.textBaseline = "top";
    ctx.fillText(text, 0, 0, width);
    return canvas.toDataURL("image/png");
}

/**
 * The lines the browser broke a text node into, with the box each one occupies.
 * Measured a character at a time, because that is the only way to find out
 * which characters ended up on which line.
 */
function lineBoxesOf(node) {
    const text = node.nodeValue || "";
    const range = document.createRange();
    const lines = [];
    let current = null;

    for (let i = 0; i < text.length; i++) {
        range.setStart(node, i);
        range.setEnd(node, i + 1);
        const rect = range.getClientRects()[0];
        // A collapsed space has no box; it belongs to the line before it.
        if (!rect) {
            if (current) current.text += text[i];
            continue;
        }
        if (current && Math.abs(rect.top - current.top) < 1.5) {
            current.text += text[i];
            current.right = Math.max(current.right, rect.right);
        } else {
            current = {
                top: rect.top,
                bottom: rect.bottom,
                left: rect.left,
                right: rect.right,
                text: text[i],
            };
            lines.push(current);
        }
    }
    return lines;
}

function paintBoxes(pdf, root, originX, originY) {
    for (const el of root.querySelectorAll("*")) {
        const cs = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) continue;

        const bg = rgba(cs.backgroundColor);
        if (bg[3] > 0.02) {
            pdf.setFillColor(bg[0], bg[1], bg[2]);
            // A pill or a rounded card keeps its corners; jsPDF has no per-corner radii,
            // so the largest one stands for all four.
            const radius = Math.min(
                Math.max(
                    parseFloat(cs.borderTopLeftRadius) || 0,
                    parseFloat(cs.borderTopRightRadius) || 0,
                    parseFloat(cs.borderBottomLeftRadius) || 0,
                    parseFloat(cs.borderBottomRightRadius) || 0,
                ),
                rect.width / 2,
                rect.height / 2,
            );
            if (radius > 0.5) {
                pdf.roundedRect(rect.left - originX, rect.top - originY, rect.width, rect.height, radius, radius, "F");
            } else {
                pdf.rect(rect.left - originX, rect.top - originY, rect.width, rect.height, "F");
            }
        }

        // The section rules, and whatever else a template draws with a border.
        for (const side of ["Top", "Bottom", "Left", "Right"]) {
            const width = parseFloat(cs[`border${side}Width`]) || 0;
            if (!width || cs[`border${side}Style`] === "none") continue;
            const colour = rgba(cs[`border${side}Color`]);
            if (colour[3] < 0.02) continue;
            pdf.setDrawColor(colour[0], colour[1], colour[2]);
            pdf.setLineWidth(width);
            if (side === "Top" || side === "Bottom") {
                const y = side === "Top"
                    ? rect.top - originY + width / 2
                    : rect.bottom - originY - width / 2;
                pdf.line(rect.left - originX, y, rect.right - originX, y);
            } else {
                const x = side === "Left"
                    ? rect.left - originX + width / 2
                    : rect.right - originX - width / 2;
                pdf.line(x, rect.top - originY, x, rect.bottom - originY);
            }
        }
    }
}

async function paintPictures(pdf, root, originX, originY) {
    for (const img of root.querySelectorAll("img")) {
        const rect = img.getBoundingClientRect();
        if (!rect.width || !img.src) continue;
        try {
            pdf.addImage(img.src, rect.left - originX, rect.top - originY, rect.width, rect.height, undefined, "FAST");
        } catch {
            // A picture that will not decode must not cost anyone their CV.
        }
    }
    for (const svg of root.querySelectorAll("svg")) {
        const rect = svg.getBoundingClientRect();
        if (!rect.width || !rect.height) continue;
        try {
            const png = await svgToPng(svg, rect.width, rect.height);
            pdf.addImage(png, "PNG", rect.left - originX, rect.top - originY, rect.width, rect.height, undefined, "FAST");
        } catch {
            /* an icon is not worth failing the export over */
        }
    }
}

function paintText(pdf, root, originX, originY) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.nodeValue && node.nodeValue.trim()) nodes.push(node);
    }

    for (const node of nodes) {
        const el = node.parentElement;
        if (!el) continue;
        const cs = window.getComputedStyle(el);
        if (cs.visibility === "hidden" || cs.display === "none") continue;
        const colour = rgba(cs.color);
        if (colour[3] < 0.02) continue;

        const size = parseFloat(cs.fontSize);
        const shorthand = fontShorthand(cs);
        const { asc, desc } = fontMetrics(shorthand);
        const [family, style] = pdfFontFor(cs);
        const href = el.closest("a[href]")?.href || "";

        for (const line of lineBoxesOf(node)) {
            // The DOM holds the text as typed; `text-transform` only changes how it is
            // painted, so the PDF has to apply it itself or "Experience" stays lower case.
            const typed = line.text.replace(/\s+$/, "");
            const text = cs.textTransform === "uppercase" ? typed.toUpperCase()
                : cs.textTransform === "lowercase" ? typed.toLowerCase()
                    : typed;
            if (!text.trim()) continue;

            const x = line.left - originX;
            const width = line.right - line.left;
            const height = line.bottom - line.top;
            // Where the browser put the baseline inside the line box: the box
            // is taller than the type by the leading, which sits half above.
            const leading = Math.max(0, (height - (asc + desc)) / 2);
            const baseline = line.top - originY + leading + asc;

            if (!CORE_FONT_RANGE.test(text)) {
                const png = lineToPng(text, cs, width, asc + desc);
                pdf.addImage(png, "PNG", x, line.top - originY + leading, width, asc + desc, undefined, "FAST");
                continue;
            }

            pdf.setFont(family, style);
            pdf.setFontSize(size);
            pdf.setTextColor(colour[0], colour[1], colour[2]);

            // Times is not Georgia, so the same words come out a different
            // width. A small difference is taken up between the letters, which
            // lands the line exactly where the preview ends it and keeps the
            // right-hand column and the wrapping in agreement with the screen.
            const natural = pdf.getStringUnitWidth(text) * size;
            const gaps = Math.max(1, text.length - 1);
            const stretch = natural > 0 && width > 0 ? (width - natural) / gaps : 0;
            const fits = Math.abs(stretch) <= MAX_CHAR_SPACE;
            // A line too wide to close that way keeps its natural spacing and is
            // hung off whichever edge the preview aligns it to, so the slack
            // falls where it is least visible rather than inside the words.
            const align = fits ? "left" : cs.textAlign;
            const anchorX = align === "center" ? x + width / 2 : align === "right" ? x + width : x;

            pdf.setCharSpace(fits ? stretch : 0);
            pdf.text(text, anchorX, baseline, {
                align: align === "center" ? "center" : align === "right" ? "right" : "left",
            });
            pdf.setCharSpace(0);

            if (href) pdf.link(x, line.top - originY, width, height, { url: href });
        }
    }
}

/**
 * @param {HTMLElement[]} sheets the `.ats-cv-page-sheet` elements of the preview
 * @returns {Promise<import("jspdf").jsPDF>}
 */
export async function renderAtsCvSheetsToPdf(sheets) {
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });

    // The preview is drawn inside a scaled canvas, so the sheets on screen are
    // not at print size. Each one is copied out to a host of its own at 1:1,
    // where a measured rectangle is already a rectangle of the page.
    const host = document.createElement("div");
    host.setAttribute("aria-hidden", "true");
    host.style.cssText = [
        "position:fixed",
        "left:-20000px",
        "top:0",
        `width:${PAGE_W}px`,
        "background:#ffffff",
        "pointer-events:none",
        "z-index:-1",
    ].join(";");
    document.body.appendChild(host);

    try {
        for (let i = 0; i < sheets.length; i++) {
            if (i > 0) pdf.addPage();

            const clone = sheets[i].cloneNode(true);
            clone.querySelectorAll(".ats-cv-page-sheet__badge").forEach((el) => el.remove());
            Object.assign(clone.style, {
                transform: "none",
                boxShadow: "none",
                border: "none",
                margin: "0",
                width: `${PAGE_W}px`,
                height: `${PAGE_H}px`,
                overflow: "hidden",
                boxSizing: "border-box",
            });

            host.replaceChildren(clone);
            materialisePseudoText(clone);
            await document.fonts.ready;

            const base = clone.getBoundingClientRect();
            paintBoxes(pdf, clone, base.left, base.top);
            await paintPictures(pdf, clone, base.left, base.top);
            paintText(pdf, clone, base.left, base.top);
        }
    } finally {
        host.remove();
    }

    return pdf;
}
